// Pending reviews cell — operational heart of the officer notebook.
// Phase 2.1 rebuild: list-of-cards → table + detail flyout.
//
// Approve flow (§5.5):
//   1. POST kyc.officer_attested → projector writes kyc-approval.yaml
//   2. POST kyc.verified         → guard cleared; lifecycle advances;
//                                  completed_today gains an entry
//
// Reject flow is symmetric (kyc.officer_rejected → kyc.rejected).
// Per Phase 4's queue convention the pending file persists as audit;
// the cell tracks per-session decisions in `decided` so the user
// gets immediate visual confirmation without waiting for the
// architectural cleanup that retires pending entries (Phase 6+).
//
// Deferred per Phase 2.1 spec (option A — UI ahead of data):
// - Stat tiles beyond "Awaiting review" (need SLA/flag/attested-today aggregator).
// - Flagged filter bucket (no flag-review event yet).
// - Attested-today bucket (no aggregate).
// - CheckList in flyout (no auto-derived check items from review body yet).

import { useEffect, useMemo, useState } from 'react';
import { useOfficerQueue, type ExpandedReview } from '../../lib/officer-queue';
import { approveKyc, rejectKyc, type Decision, type ProjectorResponse } from '../../lib/events';
import { useToast } from '@rmint/shell';
import { useEventStream } from '@rmint/shell';
import { CellShell } from './CellShell';
import {
  Button,
  Pill,
  StatTile,
  FilterBar,
  Table,
  TableHead,
  TableHeadCell,
  TableBody,
  TableRow,
  TableCell,
  DetailFlyout,
  KVGrid,
} from '@rmint/ui';

type Props = { officerId: string };

type Outcome = 'approved' | 'rejected' | 'error';

type DecisionState = Record<
  string,
  { status: 'pending' | 'in-flight' | Outcome; message?: string }
>;

type Filter = 'all' | 'pending';

export function PendingReviewsCell({ officerId }: Props) {
  const { data, loading, error, reload } = useOfficerQueue(officerId, { expand: true });
  const [decided, setDecided] = useState<DecisionState>({});
  const [filter, setFilter] = useState<Filter>('all');
  const [openPath, setOpenPath] = useState<string | null>(null);

  // §6.5 SSE wiring: auto-reload on kyc.* / review.* events.
  const { events: kycEvents } = useEventStream({ typePrefix: 'kyc.' });
  const { events: reviewEvents } = useEventStream({ typePrefix: 'review.' });

  useEffect(() => {
    if (kycEvents.length > 0 || reviewEvents.length > 0) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kycEvents.length, reviewEvents.length]);

  const expanded = useMemo(() => data?.pending_expanded ?? [], [data]);

  const visible = useMemo(() => {
    if (filter === 'all') return expanded;
    return expanded.filter((r) => {
      const state = decided[r.path]?.status ?? 'pending';
      return state === 'pending' || state === 'in-flight';
    });
  }, [expanded, filter, decided]);

  const pendingCount = expanded.filter(
    (r) => (decided[r.path]?.status ?? 'pending') === 'pending' || decided[r.path]?.status === 'in-flight',
  ).length;

  const openReview = openPath ? expanded.find((r) => r.path === openPath) ?? null : null;

  return (
    <CellShell
      name="pending-reviews"
      title="Review queue"
      purpose="Subscriptions awaiting compliance attestation. Sorted by submission order; SLA + flag enrichment lands when backend supports it."
      status={
        <Button variant="ghost" size="sm" onClick={reload} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </Button>
      }
    >
      {error ? (
        <Pill tone="danger" glyph="⚠">
          {error}
        </Pill>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
        <StatTile
          label="Awaiting review"
          value={pendingCount}
          secondary={
            data ? `${expanded.length} total · ${expanded.length - pendingCount} resolved this session` : '…'
          }
          intent={pendingCount > 0 ? 'urgent' : 'neutral'}
        />
        <StatTile label="SLA breaching" value="—" secondary="data lands with SLA aggregator" />
        <StatTile label="Attested today" value="—" secondary="data lands with daily counter" />
      </div>

      <FilterBar
        className="mb-4"
        options={[
          { value: 'all' as const, label: 'All', count: expanded.length },
          { value: 'pending' as const, label: 'Pending', count: pendingCount },
        ]}
        value={filter}
        onChange={setFilter}
      />

      {!data ? (
        <LoadingTable />
      ) : visible.length === 0 ? (
        <EmptyState filter={filter} />
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeadCell>Subject</TableHeadCell>
              <TableHeadCell>Investor</TableHeadCell>
              <TableHeadCell>Phase</TableHeadCell>
              <TableHeadCell>Submitted</TableHeadCell>
              <TableHeadCell align="right">Actions</TableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((r) => {
              const summary = summarizeReview(r.body);
              const state = decided[r.path];
              const status = state?.status ?? 'pending';
              const isFlagged = status === 'error';
              const isResolved = status === 'approved' || status === 'rejected';
              const variant = isFlagged ? 'flagged' : openPath === r.path ? 'selected' : 'default';
              return (
                <TableRow
                  key={r.path}
                  variant={variant}
                  onClick={() => setOpenPath(r.path)}
                >
                  <TableCell>
                    <div className="flex flex-col gap-0.5">
                      <span className="text-foreground font-medium">{summary.title}</span>
                      {summary.subtitle ? (
                        <span className="text-xs text-foreground-mute">{summary.subtitle}</span>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell variant="mono">{investorOf(r) ?? '—'}</TableCell>
                  <TableCell>
                    <PhaseStatusPill status={status} kind={summary.kind} />
                  </TableCell>
                  <TableCell variant="mono">{filename(r.path)}</TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-2">
                      {!isResolved ? (
                        <RowActions
                          review={r}
                          officerId={officerId}
                          inFlight={status === 'in-flight'}
                          onStart={() => setDecided((prev) => ({ ...prev, [r.path]: { status: 'in-flight' } }))}
                          onResolve={(outcome, message) =>
                            setDecided((prev) => ({ ...prev, [r.path]: { status: outcome, message } }))
                          }
                          onReload={reload}
                        />
                      ) : (
                        <Pill tone={status === 'approved' ? 'success' : 'danger'}>
                          {status === 'approved' ? 'Approved' : 'Rejected'}
                        </Pill>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {data && data.pagination.hasMore ? (
        <p className="text-xs text-foreground-mute mt-3">
          More items available — pagination affordance lands in §5.5.
        </p>
      ) : null}

      <p className="text-xs text-foreground-soft border-t border-border-soft pt-3 mt-4">
        Tip: pending entries persist after a decision as an audit pointer; watch{' '}
        <strong>completed_today</strong> on the Dashboard tile to see the round-trip land.
      </p>

      <DetailFlyout
        open={!!openReview}
        onClose={() => setOpenPath(null)}
        title={openReview ? summarizeReview(openReview.body).title : ''}
        subtitle={openReview ? filename(openReview.path) : ''}
        actions={
          openReview ? (
            <DetailActions
              review={openReview}
              officerId={officerId}
              decided={decided}
              onStart={() => setDecided((prev) => ({ ...prev, [openReview.path]: { status: 'in-flight' } }))}
              onResolve={(outcome, message) =>
                setDecided((prev) => ({
                  ...prev,
                  [openReview.path]: { status: outcome, message },
                }))
              }
              onReload={reload}
              onClose={() => setOpenPath(null)}
            />
          ) : null
        }
      >
        {openReview ? <FlyoutBody review={openReview} /> : null}
      </DetailFlyout>
    </CellShell>
  );
}

// ─── Sub-components ──────────────────────────────────────────────

function PhaseStatusPill({ status, kind }: { status: 'pending' | 'in-flight' | Outcome; kind: string }) {
  if (status === 'in-flight') return <Pill tone="info" glyph="⏱">Working…</Pill>;
  if (status === 'approved') return <Pill tone="success" glyph="✓">Approved</Pill>;
  if (status === 'rejected') return <Pill tone="danger" glyph="⚠">Rejected</Pill>;
  if (status === 'error') return <Pill tone="warn" glyph="⚠">Error</Pill>;
  return <Pill tone="warn" glyph="⏱">{kind}</Pill>;
}

function RowActions({
  review,
  officerId,
  inFlight,
  onStart,
  onResolve,
  onReload,
}: {
  review: ExpandedReview;
  officerId: string;
  inFlight: boolean;
  onStart: () => void;
  onResolve: (outcome: Outcome, message?: string) => void;
  onReload: () => void;
}) {
  const toast = useToast();
  const decision = decisionFromReview(review, officerId);

  async function handle(action: 'approve' | 'reject', e: React.MouseEvent) {
    e.stopPropagation();
    if (!decision) return;
    onStart();
    try {
      const res = action === 'approve'
        ? await approveKyc(decision)
        : await rejectKyc({ ...decision, reason: 'rejected via officer notebook' });
      handleResult(res, action === 'approve' ? 'approved' : 'rejected', decision, onResolve, onReload, toast);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      onResolve('error', msg);
      toast.push({ intent: 'error', message: action === 'approve' ? 'Approve failed' : 'Reject failed', detail: msg });
    }
  }

  return (
    <>
      <Button variant="primary" size="sm" disabled={inFlight || !decision} onClick={(e) => handle('approve', e)}>
        Attest
      </Button>
      <Button variant="danger" size="sm" disabled={inFlight || !decision} onClick={(e) => handle('reject', e)}>
        Reject
      </Button>
    </>
  );
}

function DetailActions({
  review,
  officerId,
  decided,
  onStart,
  onResolve,
  onReload,
  onClose,
}: {
  review: ExpandedReview;
  officerId: string;
  decided: DecisionState;
  onStart: () => void;
  onResolve: (outcome: Outcome, message?: string) => void;
  onReload: () => void;
  onClose: () => void;
}) {
  const status = decided[review.path]?.status ?? 'pending';
  if (status === 'approved' || status === 'rejected') {
    return (
      <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
    );
  }
  return (
    <RowActions
      review={review}
      officerId={officerId}
      inFlight={status === 'in-flight'}
      onStart={onStart}
      onResolve={(outcome, message) => {
        onResolve(outcome, message);
        if (outcome !== 'error') onClose();
      }}
      onReload={onReload}
    />
  );
}

function FlyoutBody({ review }: { review: ExpandedReview }) {
  const body = review.body as Record<string, unknown> | null;
  const subject = (body?.subject ?? {}) as Record<string, unknown>;
  const data = (body?.data ?? {}) as Record<string, unknown>;
  return (
    <>
      <section>
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-foreground-mute mb-2">Snapshot</h3>
        <KVGrid
          rows={[
            { label: 'Investor', value: <code className="font-mono">{str(subject, 'investorId') ?? '—'}</code> },
            { label: 'Restaurant', value: <code className="font-mono">{str(subject, 'rmintId') ?? '—'}</code> },
            { label: 'Event', value: <code className="font-mono">{typeof body?.type === 'string' ? body.type : 'review'}</code> },
            { label: 'Stripe session', value: <code className="font-mono">{str(data, 'stripeSessionId') ?? '—'}</code> },
          ]}
        />
      </section>
      <section>
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-foreground-mute mb-2">Raw body</h3>
        <pre className="text-[11px] bg-muted border border-border-soft rounded-input p-3 overflow-auto max-h-64 text-foreground">
          {JSON.stringify(review.body, null, 2)}
        </pre>
      </section>
    </>
  );
}

function LoadingTable() {
  return (
    <div className="rounded-card border border-border overflow-hidden">
      <div className="bg-muted px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-foreground-mute">
        Loading…
      </div>
      <div className="divide-y divide-border-soft">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-14 bg-card animate-pulse" />
        ))}
      </div>
    </div>
  );
}

function EmptyState({ filter }: { filter: Filter }) {
  return (
    <div className="rounded-card border border-dashed border-border-strong bg-card-warm p-8 text-center text-sm text-foreground-mute">
      {filter === 'pending' ? 'No pending reviews. Inbox zero ⌐□_□' : 'Queue is empty.'}
    </div>
  );
}

// ─── Helpers (verbatim from prior version) ────────────────────────

type ReviewSummary = { kind: string; title: string; subtitle?: string };

function summarizeReview(body: unknown): ReviewSummary {
  if (!body || typeof body !== 'object') return { kind: 'review', title: 'pending review' };
  const b = body as Record<string, unknown>;
  const eventType =
    typeof b.type === 'string' ? b.type : typeof b.eventType === 'string' ? b.eventType : 'review';
  const subject = (b.subject ?? {}) as Record<string, unknown>;
  const data = (b.data ?? {}) as Record<string, unknown>;
  if (eventType === 'kyc.submitted') {
    return {
      kind: 'KYC',
      title: `KYC submission · investor ${str(subject, 'investorId') ?? '?'}`,
      subtitle: str(data, 'stripeSessionId')
        ? `Stripe ${str(data, 'stripeSessionId')}`
        : str(data, 'submittedAt') ?? undefined,
    };
  }
  if (eventType === 'purchase.recorded') {
    return {
      kind: 'Purchase',
      title: `Purchase · investor ${str(subject, 'investorId') ?? '?'}`,
      subtitle: str(data, 'amountUsd') ? `$${str(data, 'amountUsd')}` : undefined,
    };
  }
  if (eventType === 'fiduciary.decision_requested') {
    return {
      kind: 'Fiduciary',
      title: str(data, 'subject') ?? 'Fiduciary decision',
      subtitle: str(data, 'rationale'),
    };
  }
  return {
    kind: shortenEventType(eventType),
    title: eventType,
    subtitle: typeof b.eventId === 'string' ? `event ${b.eventId}` : undefined,
  };
}

function decisionFromReview(review: ExpandedReview, officerId: string): Decision | null {
  const body = review.body;
  if (!body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  const subject = (b.subject ?? {}) as Record<string, unknown>;
  const eventId = typeof b.eventId === 'string' ? b.eventId : filenameStem(review.path);
  const investorId = str(subject, 'investorId');
  const rmintId = str(subject, 'rmintId');
  if (!investorId || !rmintId) return null;
  const subscriptionId =
    str(b, 'entityId') ?? str(b, 'subscriptionId') ?? `sub_${investorId}`;
  const reviewId = `rev_${eventId}`;
  return { subscriptionId, investorId, rmintId, officerId, reviewId };
}

function investorOf(review: ExpandedReview): string | undefined {
  const body = review.body as Record<string, unknown> | null;
  const subject = (body?.subject ?? {}) as Record<string, unknown>;
  return str(subject, 'investorId');
}

function shortenEventType(t: string): string {
  const dot = t.indexOf('.');
  return dot > 0 ? t.slice(0, dot) : t;
}

function str(o: Record<string, unknown>, k: string): string | undefined {
  const v = o[k];
  return typeof v === 'string' ? v : typeof v === 'number' ? String(v) : undefined;
}

function filename(path: string): string {
  return path.split('/').pop() ?? path;
}

function filenameStem(path: string): string {
  return filename(path).replace(/\.yaml$/, '');
}

function handleResult(
  res: ProjectorResponse,
  outcome: Outcome,
  decision: Decision | null,
  onResolve: (outcome: Outcome, message?: string) => void,
  onReload: () => void,
  toast: ReturnType<typeof useToast>,
) {
  if (res.ok === true) {
    onResolve(outcome);
    onReload();
    toast.push({
      intent: 'success',
      message:
        outcome === 'approved'
          ? `KYC approved · investor ${decision?.investorId ?? '?'}`
          : `KYC rejected · investor ${decision?.investorId ?? '?'}`,
      detail: 'Queue refreshed; check Dashboard for completed_today count.',
    });
  } else {
    const detail = `${res.error.kind}: ${res.error.reason ?? JSON.stringify(res.error)}`;
    onResolve('error', detail);
    toast.push({
      intent: 'error',
      message: outcome === 'approved' ? 'Approve rejected by projector' : 'Reject rejected by projector',
      detail,
    });
  }
}
